import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { Marquee } from "@/components/Marquee";
import { COMPANY, PRODUCTS } from "@/data/site";
import NAPKINS from "@/data/napkins.json";

export default function Home() {
  const heroImg = "/hero/hero-bag.jpg";
  const bento = PRODUCTS.slice(0, 3);
  const napkinFeat = NAPKINS.slice(0, 5);

  return (
    <PageShell>
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden pt-14 md:pt-20 pb-24 md:pb-40"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7 reveal">
            <p className="overline">Est. India · Global Export</p>
            <h1 className="mt-8 font-serif text-6xl md:text-7xl lg:text-[112px] leading-[0.9] tracking-tighter text-[#1C2C22]">
              Packaging<br />
              that <span className="italic text-[#C27A62]">breathes.</span>
            </h1>
            <p className="mt-8 max-w-lg text-lg text-[#5C665C] leading-relaxed">
              {COMPANY.name} manufactures compostable paper bags, plates,
              tissue and disposable-ware — engineered in India, shipped to the world.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <Link
                to="/products"
                data-testid="hero-cta-products"
                className="inline-flex items-center gap-3 bg-[#1C2C22] text-[#F4F1EB] px-7 py-4 text-xs uppercase tracking-[0.25em] btn-lift hover:bg-[#C27A62]"
              >
                Explore the catalog
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="text-sm uppercase tracking-[0.2em] link-underline text-[#1C2C22]"
              >
                Request a quote
              </Link>
            </div>
          </div>

          <div className="md:col-span-5 relative reveal">
            <div className="absolute -top-8 -right-6 hero-block w-40 h-40 md:w-56 md:h-56 z-0" />
            <div className="relative z-10 zoom-wrap border border-[#D1C9B8]">
              <img
                src={heroImg}
                alt="KS Infinity Merry Christmas metallic polyester lamination bag"
                className="w-full h-[520px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee
        items={[
          "100% Biodegradable",
          "Global Export",
          "Premium Craft",
          "Sustainable Sourcing",
          "Made in India",
        ]}
      />

      {/* BENTO SHOWCASE */}
      <section
        data-testid="bento-showcase"
        className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32"
      >
        <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
          <div>
            <p className="overline">Our craft</p>
            <h2 className="font-serif text-5xl md:text-6xl tracking-tight mt-4">
              Signature products.
            </h2>
          </div>
          <Link
            to="/products"
            data-testid="bento-link-all"
            className="text-sm uppercase tracking-[0.25em] link-underline"
          >
            View all →
          </Link>
        </div>

        <div className="grid md:grid-cols-12 gap-6">
          <Link
            to="/products"
            data-testid="bento-card-1"
            className="md:col-span-7 md:row-span-2 relative aspect-[16/12] md:aspect-auto md:min-h-[540px] zoom-wrap group border border-[#D1C9B8]"
          >
            <img
              src={bento[0].image}
              alt={bento[0].name}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1C2C22]/70 via-[#1C2C22]/10 to-transparent" />
            <div className="absolute bottom-0 left-0 p-8 md:p-12 text-[#F4F1EB]">
              <p className="overline text-[#EBE5D9]/80">01 · {bento[0].tag}</p>
              <h3 className="font-serif text-5xl md:text-6xl mt-3 tracking-tight">
                {bento[0].name}
              </h3>
            </div>
          </Link>

          {bento.slice(1).map((p, idx) => (
            <Link
              key={p.slug}
              to="/products"
              data-testid={`bento-card-${idx + 2}`}
              className="md:col-span-5 relative aspect-[4/3] md:aspect-auto md:min-h-[260px] zoom-wrap group border border-[#D1C9B8]"
            >
              <img
                src={p.image}
                alt={p.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1C2C22]/80 via-[#1C2C22]/10 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6 md:p-8 text-[#F4F1EB]">
                <p className="overline text-[#EBE5D9]/80">
                  0{idx + 2} · {p.tag}
                </p>
                <h3 className="font-serif text-3xl md:text-4xl mt-2 tracking-tight">
                  {p.name}
                </h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* SEASONAL COLLECTION TEASER */}
      <section
        data-testid="home-napkins-teaser"
        className="max-w-7xl mx-auto px-6 lg:px-10 pb-24"
      >
        <Link
          to="/products/christmas-napkins"
          data-testid="home-napkins-link"
          className="group block border border-[#D1C9B8] bg-[#EBE5D9] p-6 md:p-10"
        >
          <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
            <div>
              <p className="overline text-[#C27A62]">New drop · Seasonal</p>
              <h3 className="font-serif text-4xl md:text-6xl tracking-tight mt-4">
                Napkin collection.
              </h3>
            </div>
            <span className="text-xs uppercase tracking-[0.25em] link-underline">
              View {NAPKINS.length} designs →
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {napkinFeat.map((n) => (
              <div
                key={n.code}
                className="aspect-square bg-[#F4F1EB] border border-[#D1C9B8] overflow-hidden"
              >
                <img
                  src={n.image}
                  alt={n.code}
                  loading="lazy"
                  className="w-full h-full object-contain p-4 transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            ))}
          </div>
        </Link>
      </section>

      {/* MANIFESTO STRIP */}
      <section
        data-testid="manifesto-strip"
        className="border-y border-[#D1C9B8] bg-[#EBE5D9]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="overline">The KS approach</p>
            <h3 className="font-serif text-4xl md:text-5xl tracking-tight mt-4">
              Paper is a promise —<br />
              <span className="italic text-[#C27A62]">and we keep ours.</span>
            </h3>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-8 text-[#1C2C22]">
            {[
              { k: "Sustainable Sourcing", v: "FSC & recycled pulp only." },
              { k: "In-house Printing", v: "Water-based, food-safe inks." },
              { k: "Export Ready", v: "Custom MOQ, containerised shipping." },
              { k: "Quality Audit", v: "Every batch, every SKU." },
            ].map((f) => (
              <div key={f.k} className="border-t border-[#D1C9B8] pt-6">
                <p className="overline">{f.k}</p>
                <p className="mt-3 text-lg text-[#1C2C22] font-serif">{f.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FACTORY LOCATION */}
      <section
        data-testid="factory-location"
        className="max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-28"
      >
        <div className="grid md:grid-cols-12 gap-10 items-stretch">
          <div className="md:col-span-5 flex flex-col justify-between">
            <div>
              <p className="overline">Visit the factory</p>
              <h3 className="font-serif text-5xl md:text-6xl tracking-tight mt-4 leading-[1]">
                Our <span className="italic text-[#C27A62]">home base.</span>
              </h3>
              <address
                data-testid="factory-address"
                className="not-italic mt-8 text-lg text-[#1C2C22] font-serif leading-relaxed"
              >
                KS Infinity Export
                <br />
                Plot No. J-78, UPSIDC Site 5
                <br />
                Kasna, Gautam Buddh Nagar
                <br />
                India — 201312
              </address>
            </div>
            <a
              href="https://www.google.com/maps?q=28.426313875778032,77.54452877549323"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="factory-directions-link"
              className="inline-flex items-center gap-3 mt-10 bg-[#1C2C22] text-[#F4F1EB] px-6 py-4 text-xs uppercase tracking-[0.25em] btn-lift hover:bg-[#C27A62] w-fit"
            >
              Get directions →
            </a>
          </div>
          <div className="md:col-span-7">
            <div className="w-full h-[360px] md:h-[440px] border border-[#D1C9B8] overflow-hidden">
              <iframe
                data-testid="factory-map"
                title="KS Infinity Export factory location"
                src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d2553.9065736210823!2d77.54452877549323!3d28.426313875778032!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMjjCsDI1JzM0LjciTiA3N8KwMzInNDkuNiJF!5e1!3m2!1sen!2sin!4v1784636895860!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
